import { useRef, type RefObject } from 'react';
import { toast } from 'sonner';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import type { ModalFC } from '@/components/modals/modalsManager';
import { Dialog } from '@/components/ui/dialog';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { IconButton } from '@/components/ui/IconButton';
import { Box, XStack, YStack } from '@/components/ui/layout/layout';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import {
  buildShareUrl,
  copyShareImage,
  downloadShareImage,
  openTweetIntent,
  shareFilename,
} from './growth-share';
import { GrowthShareCard } from './growth-share-card';
import { useWrappedReport } from './growth.hooks';
import { useCreateWrappedShareMutation } from './growth.query';

import type { WrappedReportData } from './growth.types';

const HEATMAP_COLORS = [
  'bg-foreground/[0.06]',
  'bg-blue-400/40 dark:bg-blue-500/30',
  'bg-blue-500/65 dark:bg-blue-400/55',
  'bg-blue-500/90 dark:bg-blue-400/80',
  'bg-blue-500 dark:bg-blue-300',
] as const;
const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const HOUR_PERSONA_COPY: Record<string, string> = {
  early_bird: 'Early Bird',
  morning: 'Morning Person',
  afternoon: 'Afternoon Thinker',
  night_owl: 'Night Owl',
  late_night: 'Late-Night Hacker',
  explorer: 'Explorer',
};

const WEEKDAY_VS_WEEKEND_COPY: Record<string, string> = {
  weekday_warrior: 'weekday warrior',
  weekend_hacker: 'weekend hacker',
  balanced: 'balanced week',
};

const formatHour = (hour: number | null): string => {
  if (hour === null) return '—';
  const h = ((hour + 11) % 12) + 1;
  const suffix = hour < 12 ? 'am' : 'pm';
  return `${h}${suffix}`;
};

const formatDate = (iso: string | null): string | null => {
  if (!iso) return null;
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

const STAGGER_BASE =
  'motion-opacity-in-0 motion-translate-y-in-[8px] motion-duration-400 motion-ease-out';
const STAGGER_DELAYS = [
  'motion-delay-[0ms]',
  'motion-delay-[60ms]',
  'motion-delay-[120ms]',
  'motion-delay-[180ms]',
  'motion-delay-[240ms]',
  'motion-delay-[300ms]',
];
const stagger = (i: number) =>
  cn(STAGGER_BASE, STAGGER_DELAYS[i] ?? STAGGER_DELAYS.at(-1));

export const GrowthModal: ModalFC<{}> = ({ open, onOpenChange }) => {
  const { data, isLoading } = useWrappedReport();
  const shareCardRef = useRef<HTMLDivElement | null>(null);

  const hasAnyActivity =
    !!data &&
    (data.daysActive > 0 ||
      !!data.topTool ||
      data.littlebirdUsage.daysOpened > 0);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="max-w-xl w-full max-h-[92vh] bg-background overflow-y-auto scrollbar-hide p-0">
        {data && hasAnyActivity && (
          <ShareMenu data={data} shareCardRef={shareCardRef} />
        )}
        {!data ? (
          isLoading ? (
            <LoadingSkeleton />
          ) : (
            <YStack center className="py-20 px-6 gap-3 text-center">
              <span className="text-foreground-muted">
                Could not load your AI report.
              </span>
            </YStack>
          )
        ) : !hasAnyActivity ? (
          <YStack center className="py-20 px-6 gap-3 text-center">
            <span className="text-4xl">🔍</span>
            <h2 className="text-lg font-serif text-foreground">
              No AI activity yet
            </h2>
            <span className="text-sm text-foreground-muted max-w-xs">
              Start using AI tools and Littlebird will track your usage here.
              Check back after a few days.
            </span>
          </YStack>
        ) : (
          <WrappedBody data={data} />
        )}
        {data && hasAnyActivity && (
          <div
            aria-hidden
            style={{
              position: 'fixed',
              left: -99999,
              top: 0,
              pointerEvents: 'none',
            }}
          >
            <GrowthShareCard ref={shareCardRef} data={data} />
          </div>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
};

type BodyProps = { data: WrappedReportData };

export const WrappedBody = ({ data }: BodyProps) => (
  <YStack className="px-4 sm:px-6 pt-6 pb-6 gap-7">
    <Masthead data={data} />
    <Hero data={data} />
    <StatRibbon data={data} />
    <Middle data={data} />
    <Ticker data={data} />
  </YStack>
);

const Masthead = ({ data }: BodyProps) => (
  <XStack center className={cn('gap-2 pr-14', stagger(0))}>
    <AppLogo size={14} />
    <span className="text-[11px] font-semibold tracking-[0.22em] uppercase text-foreground-muted">
      {data.periodLabel} AI Report
    </span>
  </XStack>
);

const Hero = ({ data }: BodyProps) => (
  <YStack className={cn('gap-3 items-center text-center', stagger(1))}>
    <h1 className="text-[22px] leading-[1.15] font-serif text-foreground tracking-tight max-w-md">
      &ldquo;{data.headline}&rdquo;
    </h1>
    <XStack center className="gap-2">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-foreground/8 px-2.5 py-0.5 text-[10px] font-medium tracking-wide text-foreground border border-foreground/10 whitespace-nowrap">
        <Icons.user className="size-3 text-foreground-muted" />
        {data.persona}
      </span>
    </XStack>
    {data.topPercentile !== null && (
      <span className="text-[11px] text-foreground-muted whitespace-nowrap">
        🏆 Top{' '}
        <span className="font-semibold text-foreground tabular-nums">
          {data.topPercentile}%
        </span>{' '}
        on Littlebird
      </span>
    )}
  </YStack>
);

const StatRibbon = ({ data }: BodyProps) => {
  const top = data.topTool;
  const peakDate = formatDate(data.peakDayDate);
  return (
    <div
      className={cn(
        'grid grid-cols-2 sm:grid-cols-4 gap-3 py-2.5 border-y border-border-strong [grid-template-rows:auto_auto_auto_auto_auto_auto] sm:[grid-template-rows:auto_auto_auto]',
        stagger(2)
      )}
    >
      <Stat
        label="Top Tool"
        value={top?.name ?? '—'}
        sub={top ? `${Math.round(top.sharePct)}%` : 'no data'}
        isText
      />
      <Stat label="Streak" value={`${data.longestStreak}`} sub="days 🔥" />
      <Stat
        label="Active"
        value={`${data.daysActive}`}
        sub={`/ ${data.totalDays}`}
      />
      <Stat label="Peak Day" value={peakDate ?? '—'} sub="heaviest" isText />
    </div>
  );
};

type StatProps = {
  label: string;
  value: string;
  sub: string;
  isText?: boolean;
};

const Stat = ({ label, value, sub, isText }: StatProps) => (
  <div className="grid grid-rows-subgrid row-span-3 gap-0.5 min-w-0">
    <span className="text-[9px] font-bold tracking-[0.22em] uppercase text-foreground-muted self-end">
      {label}
    </span>
    <span
      className={cn(
        'font-bold text-foreground leading-none truncate self-center',
        isText ? 'text-[22px] font-serif' : 'text-[26px] tabular-nums'
      )}
      title={value}
    >
      {value}
    </span>
    <span className="text-[10px] text-foreground-muted truncate self-start">
      {sub}
    </span>
  </div>
);

const Middle = ({ data }: BodyProps) => (
  <XStack
    className={cn(
      'flex-col items-stretch gap-7 sm:flex-row sm:items-start sm:gap-5',
      stagger(3)
    )}
  >
    <ToolkitColumn data={data} />
    <HeatmapColumn data={data} />
  </XStack>
);

const ToolkitColumn = ({ data }: BodyProps) => (
  <YStack className="gap-2 flex-1 min-w-0 basis-0">
    <XStack className="items-baseline justify-between">
      <span className="text-[9px] font-bold tracking-[0.22em] uppercase text-foreground-muted">
        AI Toolkit
      </span>
      <span className="text-[10px] text-foreground-muted">
        {data.tools.length} {data.tools.length === 1 ? 'tool' : 'tools'}
      </span>
    </XStack>
    <YStack className="gap-[7px]">
      {data.tools.length === 0 ? (
        <span className="text-xs text-foreground-muted">
          No AI tool activity yet
        </span>
      ) : (
        data.tools.map((tool) => (
          <XStack key={tool.name} center className="gap-2.5">
            <span className="text-[13px] font-medium text-foreground w-[80px] truncate">
              {tool.name}
            </span>
            <Box className="flex-1 h-[5px] rounded-full bg-foreground/[0.06] overflow-hidden">
              <Box
                className="h-full rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500 transition-all duration-[1200ms] ease-out"
                style={{ width: `${Math.max(tool.percentage, 3)}%` }}
              />
            </Box>
            <span className="text-[10px] text-foreground tabular-nums w-9 text-right">
              {Math.round(tool.percentage)}%
            </span>
          </XStack>
        ))
      )}
    </YStack>
  </YStack>
);

const HeatmapColumn = ({ data }: BodyProps) => {
  const r = data.rhythm;
  const persona = HOUR_PERSONA_COPY[r.hourPersona] ?? 'Explorer';
  const wvw = WEEKDAY_VS_WEEKEND_COPY[r.weekdayVsWeekend] ?? 'balanced';
  return (
    <YStack className="flex-1 min-w-0 basis-0 self-stretch justify-between items-center gap-2">
      <span className="self-start text-[9px] font-bold tracking-[0.22em] uppercase text-foreground-muted">
        Rhythm
      </span>
      <div className="flex-1 flex items-center justify-center w-full">
        <HeatmapGrid weeks={data.heatmapWeeks} />
      </div>
      <YStack className="gap-0.5 items-center text-center pb-0.5">
        <span className="text-[13px] font-serif text-foreground leading-tight">
          {persona}
        </span>
        <span className="text-[10px] text-foreground-muted">
          peak {formatHour(r.peakHourLocal)} &middot; {wvw}
        </span>
      </YStack>
    </YStack>
  );
};

const HeatmapGrid = ({
  weeks,
}: {
  weeks: WrappedReportData['heatmapWeeks'];
}) => (
  <XStack className="gap-[2px] items-start">
    <YStack className="gap-[2px] mr-1 shrink-0">
      {WEEKDAY_LABELS.map((label, i) => (
        <Box key={i} className="h-[11px] w-[8px] flex items-center">
          <span className="text-[7px] leading-none text-foreground-muted">
            {i % 2 === 1 ? label : ''}
          </span>
        </Box>
      ))}
    </YStack>
    {weeks.map((week, weekIdx) => (
      <YStack key={weekIdx} className="gap-[2px]">
        {week.map((cell, dayIdx) => {
          const idx = weekIdx * 7 + dayIdx;
          return cell ? (
            <Box
              key={cell.date}
              title={cell.date}
              className={cn(
                'h-[11px] w-[11px] rounded-[2px] motion-opacity-in-0 motion-scale-in-50 motion-duration-300',
                HEATMAP_COLORS[cell.level]
              )}
              style={{
                animationDelay: `${300 + idx * 10}ms`,
                animationFillMode: 'backwards',
              }}
            />
          ) : (
            <Box key={`e-${dayIdx}`} className="h-[11px] w-[11px]" />
          );
        })}
      </YStack>
    ))}
  </XStack>
);

const Ticker = ({ data }: BodyProps) => {
  const lb = data.littlebirdUsage;

  const items = [
    { label: 'chats', value: lb.chatCount },
    { label: 'msgs', value: lb.messageCount },
    { label: 'meetings', value: lb.meetingsRecorded },
  ];

  return (
    <YStack
      className={cn('gap-2 pt-4 border-t border-border-strong', stagger(4))}
    >
      <span className="text-[9px] font-bold tracking-[0.22em] uppercase text-foreground-muted">
        Your Littlebird {data.periodLabel.split(' ')[0]}
      </span>
      <div className="grid grid-cols-3 gap-x-3 gap-y-1.5">
        {items.map((it) => (
          <MicroStat key={it.label} {...it} />
        ))}
      </div>
    </YStack>
  );
};

type MicroStatProps = { label: string; value: number };

const MicroStat = ({ label, value }: MicroStatProps) => (
  <XStack className="items-baseline gap-1.5 min-w-0">
    <span className="text-[15px] font-semibold text-foreground tabular-nums">
      {value}
    </span>
    <span className="text-[10px] text-foreground-muted uppercase tracking-[0.15em] truncate">
      {label}
    </span>
  </XStack>
);

type ShareMenuProps = {
  data: WrappedReportData;
  shareCardRef: RefObject<HTMLDivElement | null>;
};

const ShareMenu = ({ data, shareCardRef }: ShareMenuProps) => {
  const createShare = useCreateWrappedShareMutation();

  const withNode = async (fn: (node: HTMLElement) => Promise<void>) => {
    const node = shareCardRef.current;
    if (!node) return;
    try {
      await fn(node);
    } catch (err) {
      console.error('AI Wrapped share failed', err);
      toast.error('Could not generate share image');
    }
  };

  const handleDownload = () =>
    withNode(async (node) => {
      await downloadShareImage(node, shareFilename(data.periodLabel));
      toast.success('Image downloaded');
    });

  const handleCopy = () =>
    withNode(async (node) => {
      const ok = await copyShareImage(node);
      if (ok) toast.success('Image copied to clipboard');
      else {
        await downloadShareImage(node, shareFilename(data.periodLabel));
        toast.success('Image downloaded (clipboard unsupported)');
      }
    });

  const handleTweet = async () => {
    try {
      const { shareId } = await createShare.mutateAsync();
      openTweetIntent(
        data.periodLabel,
        data.topTool?.name ?? 'AI',
        data.daysActive,
        buildShareUrl(shareId)
      );
    } catch (err) {
      console.error('AI Wrapped share link failed', err);
      toast.error('Could not create share link');
    }
  };

  const handleCopyLink = async () => {
    try {
      const { shareId } = await createShare.mutateAsync();
      await navigator.clipboard.writeText(buildShareUrl(shareId));
      toast.success('Link copied to clipboard');
    } catch (err) {
      console.error('AI Wrapped share link failed', err);
      toast.error('Could not copy share link');
    }
  };

  return (
    <div className="absolute right-12 top-3 z-10">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <IconButton
            icon={Icons.share}
            size="sm"
            variant="ghost"
            tooltip="Share"
            aria-label="Share AI Wrapped"
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end">
          <DropdownMenu.Item onSelect={handleDownload}>
            <Icons.download className="size-4 mr-2" />
            Download image
          </DropdownMenu.Item>
          <DropdownMenu.Item onSelect={handleCopy}>
            <Icons.copy className="size-4 mr-2" />
            Copy image
          </DropdownMenu.Item>
          <DropdownMenu.Item onSelect={handleCopyLink}>
            <Icons.link className="size-4 mr-2" />
            Copy link
          </DropdownMenu.Item>
          <DropdownMenu.Separator />
          <DropdownMenu.Item onSelect={handleTweet}>
            <Icons.share className="size-4 mr-2" />
            Share on X
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </div>
  );
};

const LoadingSkeleton = () => (
  <YStack className="px-6 py-5 gap-5">
    <YStack className="gap-1 items-center text-center">
      <span className="text-sm font-medium">Crunching the numbers</span>
      <span className="text-xs text-foreground-muted">
        Feel free to come back later, we'll have it ready.
      </span>
    </YStack>
    <Skeleton className="h-8 w-full rounded" />
    <Skeleton className="h-16 w-full rounded" />
    <XStack className="gap-5">
      <YStack className="flex-1 gap-2">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-4 w-full rounded" />
        ))}
      </YStack>
      <Skeleton className="h-[100px] w-[180px] rounded" />
    </XStack>
    <Skeleton className="h-16 w-full rounded" />
  </YStack>
);
